import { Check, ChevronDown, Loader2, Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useFloatingMenuPosition } from "@/hooks/useFloatingMenuPosition";
import { FetchPill } from "./FetchPill";

/** The open menu's tallest size in px: the "Select all" row plus the `max-h-60` options list. Lets a
 *  `menuPosition="fixed"` menu decide whether it fits below the field or should open upward. */
const FIXED_MENU_MAX_HEIGHT_PX = 280;

export interface MultiSelectOption {
  value: string;
  label: string;
  count?: number;
  icon?: React.ReactNode;
}

interface FetchMultiselectProps {
  options: MultiSelectOption[];
  selectedValues: string[];
  onChange: (selectedValues: string[]) => void;
  placeholder?: string;
  searchable?: boolean;
  showCounts?: boolean;
  showPills?: boolean;
  className?: string;
  disabled?: boolean;
  onSearchChange?: (searchTerm: string) => void;
  disableClientFilter?: boolean;
  loading?: boolean;
  /** Optional leading icon shown inside the trigger (e.g. a Filter icon). Ignored when `searchable` because the search icon already occupies that slot. */
  leftIcon?: React.ReactNode;
  /** When true, the trigger shows the comma-joined labels of all selected options instead of the "N selected" summary. */
  joinSelectedLabels?: boolean;
  /** Accessible name for the trigger — required when there's no visible label element connected via htmlFor/aria-labelledby. */
  ariaLabel?: string;
  /** `"absolute"` (default) hangs the menu under the field. `"fixed"` pins it to the viewport at the
   *  field's position instead, so a scrolling ancestor (a filter panel's body) can't clip it. The
   *  menu stays in the same DOM tree, so an ancestor popover's outside-click check still sees it.
   *  Don't use it under a transformed ancestor: that would become the fixed menu's containing block. */
  menuPosition?: "absolute" | "fixed";
  /** When true, picking a new option from the menu clears the search text and keeps focus in the input,
   *  so the user can type the next search at once. */
  clearSearchOnPick?: boolean;
}

/**
 * Nowadays themed multi-select dropdown component.
 */
export const FetchMultiselect = ({
  options,
  selectedValues,
  onChange,
  placeholder = "Select options...",
  searchable = false,
  showCounts = false,
  showPills = true,
  className = "",
  disabled = false,
  onSearchChange,
  disableClientFilter = false,
  loading = false,
  leftIcon,
  joinSelectedLabels = false,
  ariaLabel,
  menuPosition = "absolute",
  clearSearchOnPick = false,
}: FetchMultiselectProps) => {
  // State
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputFocused, setInputFocused] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isMenuFixed = menuPosition === "fixed";
  const { position: fixedMenuPosition } = useFloatingMenuPosition(dropdownRef, {
    isOpen: isOpen && isMenuFixed,
    maxHeight: FIXED_MENU_MAX_HEIGHT_PX,
  });

  // Effects
  useEffect(() => {
    const handleClickOutside = (event: PointerEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm("");
        setInputFocused(false);
        setFocusedIndex(0);
      }
    };

    document.addEventListener("pointerdown", handleClickOutside);
    return () => document.removeEventListener("pointerdown", handleClickOutside);
  }, []);

  // Reset focused index when filtered options change
  useEffect(() => {
    setFocusedIndex(0);
  }, [searchTerm]);

  // Helpers
  // If using server-side filtering (onSearchChange) or disableClientFilter is true, show all options
  // Otherwise, do client-side filtering
  const filteredOptions =
    disableClientFilter || onSearchChange
      ? options
      : searchable && searchTerm
        ? options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
        : options;

  const getDisplayText = () => {
    if (searchTerm && searchable) return "";
    if (selectedValues.length === 0) return placeholder;
    if (joinSelectedLabels) {
      return selectedValues.map((value) => options.find((opt) => opt.value === value)?.label ?? value).join(", ");
    }
    if (selectedValues.length === 1) {
      const selectedOption = options.find((opt) => opt.value === selectedValues[0]);
      return selectedOption?.label || selectedValues[0];
    }
    return `${selectedValues.length} selected`;
  };

  // Handlers
  const handleToggleOption = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const handlePickOption = (value: string) => {
    const isNewPick = !selectedValues.includes(value);
    handleToggleOption(value);
    if (!clearSearchOnPick || !isNewPick) return;
    setSearchTerm("");
    onSearchChange?.("");
    inputRef.current?.focus();
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const handleSelectAll = () => {
    onChange(filteredOptions.map((option) => option.value));
  };

  const handleContainerClick = () => {
    if (disabled) return;
    setIsOpen(true);
    if (searchable && inputRef.current) {
      inputRef.current.focus();
      setInputFocused(true);
    }
  };

  // Keyboard support for the non-searchable trigger (the searchable variant's
  // input already handles this via handleInputKeyDown). Without this, the
  // trigger is only operable by mouse.
  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        return;
      }
      if (filteredOptions.length > 0) {
        handleToggleOption(filteredOptions[focusedIndex].value);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        setFocusedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : prev));
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setFocusedIndex(0);
    }
  };

  const handleInputFocus = () => {
    setInputFocused(true);
    setIsOpen(true);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      setSearchTerm("");
      setInputFocused(false);
      setFocusedIndex(0);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        setFocusedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : prev));
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filteredOptions.length > 0) {
        // Select the focused option
        const focusedOption = filteredOptions[focusedIndex];
        handlePickOption(focusedOption.value);

        // If only one option, clear search and close dropdown
        if (filteredOptions.length === 1) {
          setSearchTerm("");
          setIsOpen(false);
          setInputFocused(false);
          setFocusedIndex(0);
        }
      }
    }
  };

  return (
    <div className="w-full">
      <div className="relative" ref={dropdownRef}>
        {/* Main trigger container */}
        <div
          className={`font-medium text-sm w-full rounded-lg border bg-white cursor-pointer touch-manipulation transition-colors relative focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200 focus-visible:border-indigo-300 ${
            disabled
              ? "opacity-50 cursor-not-allowed border-gray-200"
              : `border-gray-200 hover:border-gray-300 ${isOpen ? "border-indigo-300 ring-1 ring-indigo-200" : ""}`
          } ${className}`}
          onClick={handleContainerClick}
          aria-label={ariaLabel}
          {...(!searchable && {
            role: "button",
            tabIndex: disabled ? -1 : 0,
            "aria-haspopup": "listbox",
            "aria-expanded": isOpen,
            onKeyDown: handleTriggerKeyDown,
          })}
        >
          <div className="flex items-center w-full min-h-10">
            {/* Search icon or loading indicator (when searchable), or custom leftIcon when not. */}
            {searchable ? (
              <div className="pl-3 pr-2">
                {loading ? (
                  <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                ) : (
                  <Search className="w-4 h-4 text-gray-400" />
                )}
              </div>
            ) : leftIcon ? (
              <div className="pl-3 pr-2 text-gray-400" aria-hidden="true">
                {leftIcon}
              </div>
            ) : null}

            {/* Input/Display area */}
            <div className="flex-1 min-w-0">
              {searchable ? (
                <input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    const newSearchTerm = e.target.value;
                    setSearchTerm(newSearchTerm);
                    if (onSearchChange) {
                      onSearchChange(newSearchTerm);
                    }
                  }}
                  onFocus={handleInputFocus}
                  onKeyDown={handleInputKeyDown}
                  placeholder={getDisplayText() || placeholder}
                  className="w-full py-2 pr-2 text-sm font-medium bg-transparent border-none outline-none placeholder:text-gray-400"
                  disabled={disabled}
                />
              ) : (
                <div className="px-3 py-2 text-sm font-medium truncate">
                  <span className={selectedValues.length === 0 ? "text-gray-400" : "text-gray-700"}>
                    {getDisplayText()}
                  </span>
                </div>
              )}
            </div>

            {/* Chevron */}
            <div className="pr-3">
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </div>
          </div>
        </div>

        {/* Selected pills (outside the search bar) */}
        {showPills && selectedValues.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {selectedValues.map((value) => {
              const option = options.find((opt) => opt.value === value);
              return (
                <FetchPill
                  key={value}
                  selected={true}
                  className="text-xs py-1 px-2 flex items-center gap-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleOption(value);
                  }}
                >
                  {option?.icon && <span aria-hidden="true">{option.icon}</span>}
                  <span className="truncate">{option?.label || value}</span>
                  <X className="w-3 h-3 hover:bg-black/10 rounded-sm" />
                </FetchPill>
              );
            })}
          </div>
        )}

        {/* Dropdown menu */}
        {isOpen && (
          <div
            className={`z-50 bg-white border border-gray-200 rounded-lg shadow-lg ${isMenuFixed ? "fixed" : "absolute w-full mt-1"}`}
            style={
              isMenuFixed
                ? {
                    ...(fixedMenuPosition.opensUpward
                      ? { bottom: window.innerHeight - fixedMenuPosition.top }
                      : { top: fixedMenuPosition.top }),
                    left: fixedMenuPosition.left,
                    width: fixedMenuPosition.width,
                    // Hidden until the first measurement, so it never flashes at the viewport's corner.
                    visibility: fixedMenuPosition.width === 0 ? "hidden" : undefined,
                  }
                : undefined
            }
          >
            {/* Select all / Clear all button */}
            {(selectedValues.length > 0 || filteredOptions.length > 0) && (
              <div className="px-3 py-2 border-b border-gray-100">
                <button
                  type="button"
                  onClick={selectedValues.length > 0 ? handleClearAll : handleSelectAll}
                  className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  {selectedValues.length > 0
                    ? `Clear all (${selectedValues.length})`
                    : `Select all (${filteredOptions.length})`}
                </button>
              </div>
            )}

            {/* Options list */}
            <div className="max-h-60 overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-4 text-sm font-medium text-gray-500 text-center">
                  {searchTerm ? "No options found" : "No options available"}
                </div>
              ) : (
                filteredOptions.map((option, index) => {
                  const isSelected = selectedValues.includes(option.value);
                  const isFocused = index === focusedIndex;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={`w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors flex items-center justify-between ${
                        isFocused ? "bg-gray-100" : ""
                      }`}
                      onClick={() => handlePickOption(option.value)}
                      onMouseEnter={() => setFocusedIndex(index)}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div
                          className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                            isSelected ? "bg-indigo-600 border-indigo-600" : "border-gray-300"
                          }`}
                        >
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        {option.icon && <span aria-hidden="true">{option.icon}</span>}
                        <span
                          className={`text-sm font-medium truncate ${isSelected ? "text-gray-900" : "text-gray-700"}`}
                        >
                          {option.label}
                        </span>
                      </div>
                      {showCounts && option.count !== undefined && (
                        <span className="text-xs text-gray-500 ml-2">({option.count})</span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
